import React from 'react';
import {apolloClient, isInIframe, isInJupyterNotebook} from '../setup';
import {ENTITY_QUERY, CREATE_ENTITY} from '../graphql/users';
import {push} from 'connected-react-router';
import queryString from 'query-string';
import {clearCurrentIdToken, setCurrentIdToken} from '../actions/auth';
import {displayError} from '../state/global/actions';
import {captureError} from './integrations';
import {getCookie, setCookie, unsetCookie} from './cookie';
import {WaitingAuth0Client} from '../react-auth0-spa';
import {Store} from 'redux';
import {Button} from 'semantic-ui-react';
import {authCacheLoggedIn} from '../reducers/auth';
import {History} from 'history';
import {envIsIntegration} from '../config';

/**
 * An authenticator that uses JWT tokens issued by auth0 to establish identity.
 * The tokens are automatically refreshed when nearing expiration.
 */
export class Auth0 {
  // currently this is only set in LocalAuth to detect password reset flows
  claims: any;
  // auth0Client is patched in by react-auth0-spa.tsx
  auth0Client: WaitingAuth0Client | null = null;

  // store is patched in by setup.ts
  store: Store | null = null;

  // we track these promises to ensure we only try to log in once at a time
  refreshTokenPromise: Promise<void> | null = null;
  authenticatePromise: Promise<void> | null = null;

  mustRefreshBufferMS = 30 * 1000; // 30 seconds
  checkAuthStateInterval = 1000; // 1 second

  // See https://auth0.com/docs/libraries/auth0js/v9#polling-with-checksession-
  refreshInterval = 15 * 60 * 1000; // 15 minutes

  constructor() {
    if (!envIsIntegration) {
      setInterval(this._refreshToken.bind(this), this.refreshInterval);
    }

    // If the user logs out from another tab, localStorage will reflect it.
    // If refresh fails, this tab will be logged out automatically.
    setInterval((): void => {
      if (this.loggedIn() && !authCacheLoggedIn()) {
        this._refreshToken();
      }
    }, this.checkAuthStateInterval);
  }

  login = (options: any = {}, state?: any) => {
    if (options.signup && this.loggedIn()) {
      this.store!.dispatch(push('/profile?message=signup'));
      return;
    }
    // state.from is populated by PrivateRoute or ErrorPage
    if (state?.from != null) {
      setAuthRedirect(state.from.pathname + (state.from.search || ''));
    }

    // Clear any auth state before authenticating with auth0
    this.store!.dispatch(clearCurrentIdToken());
    this.auth0Client!.logout({localOnly: true});

    options.appState = {
      ...options.appState,
      authRedirect: getAuthRedirect(),
    };
    this.auth0Client!.loginWithRedirect(options);
  };

  logout() {
    this.store!.dispatch(clearCurrentIdToken());

    // We clear auth redirect here just in case it was set accidentally while logged in.
    // This can happen when the user has multiple tabs open while logging in.
    clearAuthRedirect();

    // Loading a new page clears the apollo client since
    // it doesn't cache anything in local storage.
    this.auth0Client!.logout({returnTo: window.location.origin});
  }

  loggedIn(): boolean {
    return this._getToken() != null;
  }

  authenticate() {
    if (this._onLoginRedirect()) {
      this._handleAuthentication();
      return true;
    }
    return false;
  }

  _onLoginRedirect() {
    return (
      window.location.search.includes('code=') &&
      window.location.search.includes('state=')
    );
  }

  async _handleAuthentication() {
    // First call the inner authentication function, which fetches the auth
    // info from the hash and saves it.
    try {
      await this._authenticate();

      // Once this function is done and `this.authenticatePromise` is cleared,
      // further apollo queries can go through.
      doAuthRedirect();
    } catch {
      // we're already showing the error page
      console.log('Authenticate failed');
    }
  }

  // Ensures we have refreshed JWT
  async ensureCurrentIdToken() {
    // If we are currently authenticating, wait for that to finish.
    if (this.authenticatePromise) {
      await this.authenticatePromise;
    }
    await this._saveAuthResult();
    if (this._mustRefresh()) {
      await this._refreshToken();
    }
    return this._getToken();
  }

  async _authenticate() {
    if (this.authenticatePromise != null) {
      return this.authenticatePromise;
    }
    // Create and manage an authentication promise, because we need to know
    // to hold apollo requests until authentication or token refresh
    // is complete.
    this.authenticatePromise = new Promise(async (resolve, reject) => {
      try {
        const {appState} = await this.auth0Client!.handleRedirectCallback();
        if (appState?.authRedirect) {
          setAuthRedirect(appState.authRedirect);
        }
        await this._saveAuthResult();
      } catch (err) {
        if (!this.loggedIn()) {
          // Only panic and show this error message when the user does not have a valid JWT token.
          // Otherwise, users who are actually logged in may get this error when they end up on a
          // stale callback /login URL.
          const callsite = 'auth_authenticate';
          captureError(err, callsite, {fingerprint: [callsite]});
          this.store!.dispatch(
            displayError({
              code: 403,
              message: (
                <>
                  <p>
                    We're sorry, but there was a problem authenticating your
                    account.
                  </p>
                  <p>
                    <Button
                      icon="refresh"
                      size="small"
                      onClick={() => this.login()}
                    />{' '}
                    Please click to try again.
                  </p>
                  <p>
                    If this is not the first time you've seen this message,
                    please email{' '}
                    <a href="mailto:support@wandb.com">support@wandb.com</a>.
                  </p>
                </>
              ),
            })
          );
          reject();
        }
      }
      this.authenticatePromise = null;
      resolve();
    });
    return this.authenticatePromise;
  }

  _getToken() {
    if (this.store === null) {
      return null;
    }
    return this.store.getState().auth.currentIdToken;
  }

  _getExpiry() {
    if (this.store === null) {
      return null;
    }
    return this.store.getState().auth.currentExpiry;
  }

  _mustRefresh() {
    return this._afterExpiryMinusBuffer(this.mustRefreshBufferMS);
  }

  _afterExpiryMinusBuffer(buffer: number) {
    return (
      this._getExpiry() != null && Date.now() >= this._getExpiry() - buffer
    );
  }

  async _refreshToken() {
    if (this.refreshTokenPromise != null) {
      return this.refreshTokenPromise;
    }
    if (!this.loggedIn()) {
      return;
    }
    this.refreshTokenPromise = new Promise(resolve => {
      (async () => {
        try {
          await this.auth0Client!.getTokenSilently();
          await this._saveAuthResult();
        } catch (err) {
          this.store!.dispatch(clearCurrentIdToken());
          console.error('Error refreshing token:');
          console.error(err);
          this.login({}, {from: window.location});
        }
        this.refreshTokenPromise = null;
        resolve();
      })();
    });
    return this.refreshTokenPromise;
  }

  // _saveAuthResult is only async because auth0Client.getIdTokenClaims is async.
  // In reality, it resolves immediately because getIdTokenClaims only looks in cache.
  // See https://github.com/auth0/auth0-spa-js/blob/dd0784e227d8c787cf0a8c2b640057ae28afe295/src/Auth0Client.ts#L234
  async _saveAuthResult() {
    const authResult = await this.auth0Client!.getIdTokenClaims();

    if (!this.loggedIn() && authResult == null) {
      // Token doesn't exist / is expired and the user is no longer authenticated
      this.store!.dispatch(clearCurrentIdToken());
      return;
    }

    if (authResult != null) {
      const {__raw: idToken, exp: expiry} = authResult;
      if (idToken !== this._getToken() && expiry != null) {
        const expiryMS = expiry * 1000;
        this.store!.dispatch(setCurrentIdToken(idToken, expiryMS));
      }
    }
  }
}

export class LocalAuth {
  local = true;
  tempKey: string | undefined;
  claims: any;

  // tslint:disable-next-line:variable-name
  _store: Store | null = null;

  constructor() {
    const params = queryString.parse(document.location.search);
    if (params.jwt) {
      this.tempKey = params.jwt as string;
      this.claims = this.parseClaims(this.tempKey);
    }
  }

  login(_?: any, state?: any) {
    let backTo = '';
    if (state && state.from && state.from.pathname !== '/login') {
      backTo = state.from.pathname + (state.from.search || '');
      setAuthRedirect(backTo);
    }
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?backTo=' + backTo;
    }
  }

  logout(redirect = true) {
    fetch('/api/login', {method: 'DELETE'}).then(() => {
      this.store!.dispatch(clearCurrentIdToken());
      if (redirect) {
        window.location.href = '/';
      }
    });
  }

  parseClaims(jwt: string) {
    if (jwt === 'dummy') {
      // this is effectively a passthrough for the integration tests
      // it'll only work in integration because it relies on gorilla's
      // mock auth header, which is otherwise disabled
      return {exp: Date.now() + 60 * 60 * 24};
    }
    try {
      return JSON.parse(atob(jwt.split('.')[1]));
    } catch (e) {
      return {exp: 0};
    }
  }

  loggedIn() {
    return getCookie('id_token_2') || this.tempKey;
  }

  async remoteLogin(email: string, password: string) {
    const login = await fetch('/api/login', {
      method: 'POST',
      body: JSON.stringify({
        username: email,
        password,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (login.status === 403) {
      return false;
    } else if (login.status !== 200) {
      this.store!.dispatch(
        displayError({
          code: 500,
          message:
            'Authentication service is down, ask your administrator to check system logs',
        })
      );
      return false;
    }
    const jwt = await login.text();
    const claims = this.parseClaims(jwt);
    this.store!.dispatch(setCurrentIdToken(jwt, claims.exp * 1000));
    this.tempKey = undefined;
    return true;
  }

  async changePassword(password: string, email: string | null = null) {
    const subToEmail = this.claims.sub.split('|')[1];
    const userUpdates: Array<{[key: string]: any}> = [
      {email: email || subToEmail, password},
    ];
    if (this.claims.reset && email) {
      userUpdates.push({
        email: subToEmail,
        delete: true,
      });
    }
    const reset = await fetch('/system-admin/api/users/', {
      method: 'PUT',
      body: JSON.stringify(userUpdates),
      headers: {
        Authorization: `Bearer ${this.tempKey}`,
        'Content-Type': 'application/json',
      },
    });
    if (reset.status === 401 || reset.status === 403) {
      this.store!.dispatch(
        displayError({
          code: 403,
          message:
            'Invitation code expired, ask your administrator for a new invite',
        })
      );
      return false;
    }
    return await this.remoteLogin(email || subToEmail, password);
  }

  authenticate() {
    // TODO: perform a token refresh here?
    if (getCookie('id_token_2') === '') {
      return false;
    } else {
      return true;
    }
  }

  async autoLogin() {
    /* Attempts to login with the default user, called from index.tsx */
    if (!this.loggedIn() && window.location.pathname !== '/logout') {
      try {
        console.log('Attempting auto-login...');
        const success = await this.remoteLogin('local@wandb.com', 'perceptron');
        if (success) {
          console.log('Login succeeded');
          const res = await apolloClient.query({
            query: ENTITY_QUERY,
            variables: {name: 'local'},
          });
          if (res.data.entity.available) {
            await apolloClient.mutate({
              mutation: CREATE_ENTITY,
              variables: {name: 'local'},
            });
          }
          window.location.reload();
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  get store() {
    return this._store;
  }

  set store(store) {
    const jwt = getCookie('id_token_2');
    this._store = store;
    if (jwt !== '' && store) {
      this.claims = this.parseClaims(jwt);
      if (this.claims.reset && window.location.pathname !== '/signup') {
        if (window.location.pathname !== '/login') {
          setAuthRedirect(
            window.location.href.replace(window.location.origin, '')
          );
        }
        window.location.href = `/signup?jwt=${jwt}`;
      } else {
        store.dispatch(setCurrentIdToken(jwt, this.claims.exp * 1000));
      }
    }
  }

  async ensureCurrentIdToken() {
    // Use the temporary key if it exists to handle existing users
    return this.tempKey || getCookie('id_token_2');
  }
}

export class StubAuth {
  // currently this is only set in LocalAuth to detect password reset flows
  claims: any;
  constructor(public stubToken: string) {}

  // tslint:disable-next-line:no-empty
  login() {}

  // tslint:disable-next-line:no-empty
  logout() {}

  loggedIn() {
    return true;
  }

  authenticate() {
    return Promise.resolve();
  }

  async ensureCurrentIdToken() {
    return this.stubToken;
  }

  set store(store: Store) {
    const expiry = new Date().getTime() + 1095 * 86400 * 1000;
    store.dispatch(setCurrentIdToken(this.stubToken, expiry));
  }
}

declare global {
  interface Window {
    __WB_authRedirect?: string;
  }
}

export const AUTH_REDIRECT_KEY = 'auth.redirect';

export function getAuthRedirect(): string {
  return (
    window.__WB_authRedirect ?? localStorage.getItem(AUTH_REDIRECT_KEY) ?? '/'
  );
}

export function setAuthRedirect(url: string): void {
  if (!isValidAuthRedirect(url)) {
    return;
  }
  window.__WB_authRedirect = url;
  localStorage.setItem(AUTH_REDIRECT_KEY, url);
}

export function clearAuthRedirect(): void {
  delete window.__WB_authRedirect;
  localStorage.removeItem(AUTH_REDIRECT_KEY);
}

export function doAuthRedirect(history?: History): string {
  const url = getAuthRedirect();
  clearAuthRedirect();

  if (history != null) {
    history.replace(url);
  } else if (isInIframe() && !isInJupyterNotebook()) {
    // Logging in from loginsrv UI
    window.top.location.replace(url);
  } else {
    window.location.replace(url);
  }
  return url;
}

const INVALID_REDIRECT_PATHS = [
  '/login',
  '/logout',
  '/signup',
  '/change-password',
];

function isValidAuthRedirect(url: string): boolean {
  for (const path of INVALID_REDIRECT_PATHS) {
    if (url.startsWith(path)) {
      return false;
    }
  }
  return true;
}

const ACCESS_TOKEN_COOKIE_KEY = 'access_token';

export function setAccessTokenCookie(token: string): void {
  setCookie(ACCESS_TOKEN_COOKIE_KEY, token);
}

export function unsetAccessTokenCookie(): void {
  unsetCookie(ACCESS_TOKEN_COOKIE_KEY);
}

export function setAccessTokenCookieFromQS(): void {
  const {accessToken} = queryString.parse(window.location.search);
  if (accessToken) {
    setAccessTokenCookie(accessToken as string);
  } else {
    unsetAccessTokenCookie();
  }
}

// (un)set access token cookie on page load
setAccessTokenCookieFromQS();
